import * as Dropdown from "@radix-ui/react-dropdown-menu";
import {
  newTrackingBlock, SHOW_WHEN, trackingConfigError, type CustomerPhase, type LText, type ShowWhen, type TrackingBlock, type TrackingBlockType, type TrackingOrderView, type TrackingPageConfig, type TrackingPageLang,
} from "@logiful/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, ChevronDown, Copy, ExternalLink, Eye, Globe, HelpCircle, Image as ImageIcon, LayoutTemplate, Link2, ListChecks, Mail, Megaphone, Monitor, MousePointerClick, Package, Palette,
  Plus, RefreshCw, Rocket, Settings2, ShoppingBag, Smartphone, Sparkles, Trash2, Truck, Type, Upload, Users, X, type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, isDemo, type TrackingPageAdmin } from "@/lib/api";
import { msg, t } from "@/lib/i18n";
import { sampleTrackingOrder, TrackingView } from "@/tracking-page/TrackingView";

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Fehler")) : t("Fehler"));

const BLOCKS: Record<TrackingBlockType, { label: string; desc: string; icon: LucideIcon }> = {
  hero: { label: msg("Titelbereich"), desc: msg("Großes Banner mit Bild, Überschrift und Button"), icon: LayoutTemplate },
  status: { label: msg("Sendungsstatus"), desc: msg("Status, Fortschritt und Verlauf – Pflicht"), icon: Truck },
  eta: { label: msg("Zustellprognose"), desc: msg("Voraussichtlicher Zustelltag"), icon: Sparkles },
  items: { label: msg("Paketinhalt"), desc: msg("Bestellte Artikel mit Bildern"), icon: Package },
  promo: { label: msg("Aktion / Gutschein"), desc: msg("Angebot mit Rabattcode zum Kopieren"), icon: Megaphone },
  products: { label: msg("Produktempfehlungen"), desc: msg("Karten mit Bild, Preis und Link"), icon: ShoppingBag },
  text: { label: msg("Textblock"), desc: msg("Überschrift und Text"), icon: Type },
  faq: { label: msg("Häufige Fragen"), desc: msg("Fragen zum Aufklappen"), icon: HelpCircle },
  buttons: { label: msg("Buttons"), desc: msg("Kontakt, Retoure anmelden, Shop …"), icon: MousePointerClick },
  social: { label: msg("Social Media"), desc: msg("Links zu euren Kanälen"), icon: Users },
  newsletter: { label: msg("Newsletter"), desc: msg("Hinweis mit Anmelde-Link"), icon: Mail },
  image: { label: msg("Bild"), desc: msg("Einzelnes Bild, optional verlinkt"), icon: ImageIcon },
};
const SHOW_WHEN_LABEL: Record<ShowWhen, string> = {
  before_shipping: msg("Vor dem Versand"),
  in_transit: msg("Unterwegs"),
  out_for_delivery: msg("In Zustellung"),
  delivered: msg("Zugestellt"),
  problem: msg("Bei Problemen"),
};
const PREVIEW_PHASES: { phase: CustomerPhase; label: string }[] = [
  { phase: "processing", label: msg("Vor Versand") },
  { phase: "in_transit", label: msg("Unterwegs") },
  { phase: "out_for_delivery", label: msg("In Zustellung") },
  { phase: "delivered", label: msg("Zugestellt") },
  { phase: "problem", label: msg("Problem") },
];
const BACKGROUNDS: { id: TrackingPageConfig["theme"]["background"]; label: string }[] = [
  { id: "soft", label: msg("Sanft") },
  { id: "light", label: msg("Hell") },
  { id: "brand", label: msg("Markenfarbe") },
  { id: "dark", label: msg("Dunkel") },
];
const FONTS: { id: TrackingPageConfig["theme"]["font"]; label: string; family: string }[] = [
  { id: "modern", label: msg("Modern"), family: `"Inter Variable", sans-serif` },
  { id: "geometric", label: msg("Geometrisch"), family: `"Montserrat", sans-serif` },
  { id: "elegant", label: msg("Elegant"), family: `"Iowan Old Style", Georgia, serif` },
  { id: "rounded", label: msg("Rund"), family: `ui-rounded, "SF Pro Rounded", sans-serif` },
];
const RADII: { id: TrackingPageConfig["theme"]["radius"]; label: string }[] = [
  { id: "sharp", label: msg("Kantig") },
  { id: "soft", label: msg("Weich") },
  { id: "round", label: msg("Rund") },
];
const DOMAIN_STATUS: Record<string, { label: string; tone: "neutral" | "info" | "warning" | "success" | "danger" }> = {
  none: { label: msg("Keine"), tone: "neutral" },
  dns_pending: { label: msg("Warte auf DNS-Eintrag"), tone: "warning" },
  provisioning: { label: msg("Wird freigeschaltet"), tone: "info" },
  active: { label: msg("Aktiv"), tone: "success" },
  error: { label: msg("Fehler"), tone: "danger" },
};

const readFile = (f: File) =>
  new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] ?? "");
    r.onerror = () => rej(r.error);
    r.readAsDataURL(f);
  });

type Tab = "blocks" | "design" | "settings";

/** Tracking-Seite: Composer mit Live-Vorschau (Modul „tracking_page“) */
export function TrackingPageComposerPage() {
  const { tenantId, role } = useTenant();
  const editable = canManage(role);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tracking-page", tenantId], queryFn: () => api.trackingPage.get(tenantId) });
  const sample = useQuery({ queryKey: ["tracking-page-sample", tenantId], queryFn: () => api.trackingPage.sample(tenantId), staleTime: 5 * 60_000 });

  const [cfg, setCfg] = useState<TrackingPageConfig | null>(null);
  const [linkInShop, setLinkInShop] = useState(true);
  const [searchEnabled, setSearchEnabled] = useState(true);
  const [slug, setSlug] = useState("");
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<Tab>("blocks");
  const [selected, setSelected] = useState<string | null>(null);
  const [lang, setLang] = useState<TrackingPageLang>("de");
  const [phase, setPhase] = useState<CustomerPhase>("in_transit");
  const [device, setDevice] = useState<"mobile" | "desktop">("mobile");
  const [real, setReal] = useState(false);

  const load = (d: TrackingPageAdmin) => {
    setCfg(d.draft);
    setLinkInShop(d.linkInShop);
    setSearchEnabled(d.searchEnabled);
    setSlug(d.slug);
    setDirty(false);
  };
  useEffect(() => {
    if (q.data && !cfg) load(q.data);
  }, [q.data, cfg]);

  // ungespeicherte Änderungen nicht versehentlich verlieren
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const update = (fn: (c: TrackingPageConfig) => TrackingPageConfig) => {
    setCfg((c) => (c ? fn(structuredClone(c)) : c));
    setDirty(true);
  };
  const setBlock = (id: string, patch: (b: TrackingBlock) => TrackingBlock) => update((c) => ({ ...c, blocks: c.blocks.map((b) => (b.id === id ? patch(b) : b)) }));

  const save = useMutation({
    mutationFn: () => api.trackingPage.save(tenantId, { draft: cfg!, linkInShop, searchEnabled, slug }),
    onSuccess: (d) => {
      qc.setQueryData(["tracking-page", tenantId], d);
      setDirty(false);
      toast.success(t("Entwurf gespeichert"));
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const publish = useMutation({
    mutationFn: async () => {
      if (dirty) await api.trackingPage.save(tenantId, { draft: cfg!, linkInShop, searchEnabled, slug });
      return api.trackingPage.publish(tenantId);
    },
    onSuccess: (d) => {
      qc.setQueryData(["tracking-page", tenantId], d);
      setDirty(false);
      toast.success(t("Veröffentlicht – die Seite ist jetzt für eure Endkunden erreichbar."));
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const unpublish = useMutation({
    mutationFn: () => api.trackingPage.unpublish(tenantId),
    onSuccess: (d) => {
      qc.setQueryData(["tracking-page", tenantId], d);
      toast.success(t("Offline genommen – Versandmails verlinken wieder den Versanddienstleister."));
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  // Strg/⌘ + S speichert
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (editable) saveRef.current.mutate();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [editable]);

  const previewOrder: TrackingOrderView = useMemo(() => (real && sample.data?.order ? sample.data.order : sampleTrackingOrder(phase, lang)), [real, sample.data, phase, lang]);
  const configError = cfg ? trackingConfigError(cfg) : null;
  const d = q.data;

  if (q.isLoading || !cfg || !d)
    return (
      <div>
        <PageHeader title={msg("Tracking-Seite")} />
        <div className="px-4 md:px-8 grid lg:grid-cols-[420px_1fr] gap-5">
          <Skeleton className="h-[600px]" />
          <Skeleton className="h-[600px]" />
        </div>
      </div>
    );

  const status = d.published ? (dirty || d.changed ? { label: t("Änderungen nicht veröffentlicht"), tone: "warning" as const } : { label: t("Veröffentlicht"), tone: "success" as const }) : { label: t("Entwurf"), tone: "neutral" as const };

  return (
    <div>
      <PageHeader
        title={msg("Tracking-Seite")}
        description={t("Eure eigene Sendungsverfolgung für Endkunden – im eigenen Design, mit Live-Vorschau.")}
        actions={
          <>
            <Badge tone={status.tone}>{t(status.label)}</Badge>
            {d.published && (
              <a href={d.publicUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" icon={<ExternalLink className="size-4" />}>{t("Seite öffnen")}</Button>
              </a>
            )}
            {editable && (
              <>
                <Button variant="secondary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
                <Button variant="primary" icon={<Rocket className="size-4" />} disabled={publish.isPending || !!configError || (d.published && !dirty && !d.changed)} onClick={() => publish.mutate()}>
                  {t("Veröffentlichen")}
                </Button>
              </>
            )}
          </>
        }
      />
      <div className="px-4 md:px-8 pb-10 grid lg:grid-cols-[420px_minmax(0,1fr)] gap-5 items-start">
        {/* ── Bearbeiten ── */}
        <Card className="overflow-hidden">
          <div className="flex border-b border-border">
            {([
              ["blocks", msg("Bausteine"), ListChecks],
              ["design", msg("Design"), Palette],
              ["settings", msg("Einstellungen"), Settings2],
            ] as const).map(([k, label, Icon]) => (
              <button key={k} type="button" onClick={() => setTab(k)} className={cx("flex-1 flex items-center justify-center gap-1.5 py-3 text-[13px] font-medium border-b-2 -mb-px transition-colors", tab === k ? "border-ink text-fg" : "border-transparent text-fg-muted hover:text-fg")}>
                <Icon className="size-4" />
                {t(label)}
              </button>
            ))}
          </div>
          {tab !== "settings" && (
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border bg-surface-2/50 text-[12.5px]">
              <span className="text-fg-muted">{t("Texte bearbeiten in")}</span>
              <LangSwitch value={lang} onChange={setLang} />
            </div>
          )}
          <fieldset disabled={!editable} className="min-w-0">
            {tab === "blocks" && (
              <BlocksPanel cfg={cfg} lang={lang} selected={selected} onSelect={setSelected} update={update} setBlock={setBlock} tenantId={tenantId} />
            )}
            {tab === "design" && <DesignPanel cfg={cfg} lang={lang} update={update} tenantId={tenantId} />}
            {tab === "settings" && (
              <SettingsPanel
                d={d}
                editable={editable}
                linkInShop={linkInShop}
                searchEnabled={searchEnabled}
                slug={slug}
                onLinkInShop={(v) => { setLinkInShop(v); setDirty(true); }}
                onSearch={(v) => { setSearchEnabled(v); setDirty(true); }}
                onSlug={(v) => { setSlug(v); setDirty(true); }}
                onUnpublish={() => unpublish.mutate()}
                tenantId={tenantId}
              />
            )}
          </fieldset>
          {configError && <p className="px-4 py-3 border-t border-border text-[12.5px] text-danger flex gap-1.5"><AlertTriangle className="size-4 shrink-0" />{t(configError)}</p>}
        </Card>

        {/* ── Vorschau ── */}
        <div className="lg:sticky lg:top-4 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex rounded-lg bg-surface-2 p-0.5 text-[12.5px]">
              {PREVIEW_PHASES.map((p) => (
                <button key={p.phase} type="button" onClick={() => { setPhase(p.phase); setReal(false); }} className={cx("px-2.5 py-1 rounded-md font-medium transition-colors", !real && phase === p.phase ? "bg-surface shadow-sm text-fg" : "text-fg-muted hover:text-fg")}>
                  {t(p.label)}
                </button>
              ))}
            </div>
            {sample.data?.order && (
              <button type="button" onClick={() => setReal((v) => !v)} className={cx("px-2.5 py-1 rounded-lg text-[12.5px] font-medium border transition-colors", real ? "border-ink bg-ink text-ink-fg" : "border-border text-fg-muted hover:text-fg")}>
                {t("Letzte echte Sendung")}
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <LangSwitch value={lang} onChange={setLang} />
              <div className="flex rounded-lg bg-surface-2 p-0.5">
                {([["mobile", Smartphone], ["desktop", Monitor]] as const).map(([k, Icon]) => (
                  <button key={k} type="button" aria-label={k === "mobile" ? t("Smartphone") : t("Desktop")} onClick={() => setDevice(k)} className={cx("p-1.5 rounded-md", device === k ? "bg-surface shadow-sm text-fg" : "text-fg-muted")}>
                    <Icon className="size-4" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={cx("mx-auto rounded-[28px] border border-border bg-surface-2 p-2 shadow-sm transition-[max-width] duration-300", device === "mobile" ? "max-w-[410px]" : "max-w-none")}>
            <div className="rounded-[22px] overflow-hidden border border-border/60">
              <div className="flex items-center gap-2 px-3 py-2 bg-surface text-[11.5px] text-fg-subtle border-b border-border/60">
                <span className="flex gap-1">
                  <span className="size-2 rounded-full bg-border-strong" />
                  <span className="size-2 rounded-full bg-border-strong" />
                  <span className="size-2 rounded-full bg-border-strong" />
                </span>
                <span className="flex-1 truncate text-center font-mono">{d.publicUrl.replace(/^https:\/\//, "")}/…</span>
              </div>
              <div className="h-[calc(100vh-230px)] min-h-[520px] overflow-y-auto overscroll-contain">
                <TrackingView config={cfg} shop={d.shop} lang={lang} onLang={setLang} asset={(id) => api.trackingPage.assetUrl(id)} order={previewOrder} selectedBlock={selected} onSelectBlock={(id) => { setSelected(id); setTab("blocks"); }} narrow />
              </div>
            </div>
          </div>
          <p className="mt-2 text-center text-[12px] text-fg-subtle">{real ? t("Vorschau mit der letzten echten Sendung.") : t("Vorschau mit Beispieldaten – klicke einen Baustein an, um ihn zu bearbeiten.")}</p>
        </div>
      </div>
    </div>
  );
}

function LangSwitch({ value, onChange }: { value: TrackingPageLang; onChange: (l: TrackingPageLang) => void }) {
  return (
    <div className="flex rounded-lg bg-surface-2 p-0.5 text-[12px] font-semibold">
      {(["de", "en"] as const).map((l) => (
        <button key={l} type="button" onClick={() => onChange(l)} className={cx("px-2 py-0.5 rounded-md uppercase", value === l ? "bg-surface shadow-sm text-fg" : "text-fg-muted")}>
          {l}
        </button>
      ))}
    </div>
  );
}

// ── Bausteine ──

function BlocksPanel({ cfg, lang, selected, onSelect, update, setBlock, tenantId }: {
  cfg: TrackingPageConfig; lang: TrackingPageLang; selected: string | null; onSelect: (id: string | null) => void;
  update: (fn: (c: TrackingPageConfig) => TrackingPageConfig) => void; setBlock: (id: string, fn: (b: TrackingBlock) => TrackingBlock) => void; tenantId: string;
}) {
  const move = (i: number, dir: -1 | 1) => update((c) => {
    const b = [...c.blocks];
    const j = i + dir;
    if (j < 0 || j >= b.length) return c;
    [b[i], b[j]] = [b[j]!, b[i]!];
    return { ...c, blocks: b };
  });
  const add = (type: TrackingBlockType) => {
    const b = newTrackingBlock(type);
    const at = selected ? cfg.blocks.findIndex((x) => x.id === selected) + 1 : cfg.blocks.length;
    update((c) => ({ ...c, blocks: [...c.blocks.slice(0, at), b, ...c.blocks.slice(at)] }));
    onSelect(b.id);
  };
  const hasStatus = cfg.blocks.some((b) => b.type === "status");
  return (
    <div className="p-3 flex flex-col gap-2">
      {cfg.blocks.map((b, i) => {
        const meta = BLOCKS[b.type];
        const open = selected === b.id;
        return (
          <div key={b.id} className={cx("rounded-xl border transition-colors", open ? "border-ink/40 bg-surface shadow-sm" : "border-border bg-surface hover:border-border-strong")}>
            <div className="flex items-center gap-2.5 px-3 py-2.5">
              <button type="button" onClick={() => onSelect(open ? null : b.id)} className="flex flex-1 min-w-0 items-center gap-2.5 text-left">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-fg-muted">
                  <meta.icon className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium truncate">{t(meta.label)}</span>
                  <span className="block text-[11.5px] text-fg-subtle truncate">{b.showWhen.length ? b.showWhen.map((s) => t(SHOW_WHEN_LABEL[s])).join(" · ") : t(meta.desc)}</span>
                </span>
              </button>
              <div className="flex items-center text-fg-subtle">
                <button type="button" aria-label={t("Nach oben")} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 rounded hover:bg-surface-2 hover:text-fg disabled:opacity-30"><ArrowUp className="size-3.5" /></button>
                <button type="button" aria-label={t("Nach unten")} disabled={i === cfg.blocks.length - 1} onClick={() => move(i, 1)} className="p-1 rounded hover:bg-surface-2 hover:text-fg disabled:opacity-30"><ArrowDown className="size-3.5" /></button>
                {b.type !== "status" && (
                  <button type="button" aria-label={t("Entfernen")} onClick={() => { update((c) => ({ ...c, blocks: c.blocks.filter((x) => x.id !== b.id) })); onSelect(null); }} className="p-1 rounded hover:bg-danger-soft hover:text-danger"><Trash2 className="size-3.5" /></button>
                )}
                <ChevronDown className={cx("size-4 ml-0.5 transition-transform", open && "rotate-180")} />
              </div>
            </div>
            {open && (
              <div className="border-t border-border px-3 py-3.5 flex flex-col gap-3.5">
                <BlockEditor b={b} lang={lang} set={(fn) => setBlock(b.id, fn)} tenantId={tenantId} />
                <ShowWhenPicker value={b.showWhen} onChange={(v) => setBlock(b.id, (x) => ({ ...x, showWhen: v }))} />
              </div>
            )}
          </div>
        );
      })}
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button variant="secondary" className="mt-1 w-full justify-center" icon={<Plus className="size-4" />}>{t("Baustein hinzufügen")}</Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="start" sideOffset={6} className="z-50 w-[360px] max-h-[420px] overflow-y-auto rounded-xl border border-border bg-surface p-1.5 shadow-lg">
            {(Object.keys(BLOCKS) as TrackingBlockType[]).filter((k) => k !== "status" || !hasStatus).map((k) => {
              const m = BLOCKS[k];
              return (
                <Dropdown.Item key={k} onSelect={() => add(k)} className="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 outline-none data-[highlighted]:bg-surface-2">
                  <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-fg-muted"><m.icon className="size-4" /></span>
                  <span>
                    <span className="block text-[13px] font-medium">{t(m.label)}</span>
                    <span className="block text-[11.5px] text-fg-subtle">{t(m.desc)}</span>
                  </span>
                </Dropdown.Item>
              );
            })}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>
  );
}

function ShowWhenPicker({ value, onChange }: { value: ShowWhen[]; onChange: (v: ShowWhen[]) => void }) {
  return (
    <div>
      <p className="text-[12px] font-medium text-fg-muted mb-1.5">{t("Anzeigen")}</p>
      <div className="flex flex-wrap gap-1.5">
        <Chip active={!value.length} onClick={() => onChange([])}>{t("Immer")}</Chip>
        {SHOW_WHEN.map((s) => (
          <Chip key={s} active={value.includes(s)} onClick={() => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s])}>{t(SHOW_WHEN_LABEL[s])}</Chip>
        ))}
      </div>
    </div>
  );
}
const Chip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button type="button" onClick={onClick} className={cx("rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors", active ? "border-ink bg-ink text-ink-fg" : "border-border text-fg-muted hover:text-fg")}>{children}</button>
);

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-fg-muted">{t(label)}</span>
      {children}
      {hint && <span className="mt-1 block text-[11.5px] text-fg-subtle">{t(hint)}</span>}
    </label>
  );
}

/** Text in der gerade bearbeiteten Sprache; Englisch leer = Deutsch wird gezeigt */
function LInput({ label, value, lang, onChange, area, max, placeholder }: { label: string; value: LText; lang: TrackingPageLang; onChange: (v: LText) => void; area?: boolean; max?: number; placeholder?: string }) {
  const v = value[lang];
  const ph = lang === "en" && value.de ? value.de : placeholder ? t(placeholder) : "";
  const set = (s: string) => onChange({ ...value, [lang]: s });
  return (
    <Field label={label}>
      {area ? (
        <textarea value={v} maxLength={max} placeholder={ph} onChange={(e) => set(e.target.value)} rows={3} className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] outline-none focus:border-ink/40 focus:ring-2 focus:ring-ink/10 resize-y" />
      ) : (
        <Input value={v} maxLength={max} placeholder={ph} onChange={(e) => set(e.target.value)} />
      )}
    </Field>
  );
}

function UrlInput({ label, value, onChange, hint }: { label: string; value: string; onChange: (v: string) => void; hint?: string }) {
  const bad = !!value && !/^(https?:\/\/[^\s<>"]+|mailto:[^\s<>"]+|tel:[+\d\s()/-]+)$/i.test(value);
  return (
    <Field label={label} {...(hint ? { hint } : {})}>
      <Input value={value} invalid={bad} placeholder={t("https://")} onChange={(e) => onChange(e.target.value.trim())} leading={<Link2 className="size-3.5" />} />
      {bad && <span className="mt-1 block text-[11.5px] text-danger">{t("Link muss mit https://, mailto: oder tel: beginnen.")}</span>}
    </Field>
  );
}

function ImagePicker({ label, value, onChange, tenantId, hint }: { label: string; value: string | null; onChange: (id: string | null) => void; tenantId: string; hint?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Field label={label} {...(hint ? { hint } : {})}>
      <div className="flex items-center gap-3">
        <div className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-surface-2">
          {value ? <img src={api.trackingPage.assetUrl(value)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="size-5 text-fg-subtle" />}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="secondary" icon={busy ? <RefreshCw className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />} disabled={busy} onClick={() => ref.current?.click()}>
            {value ? t("Ersetzen") : t("Hochladen")}
          </Button>
          {value && <Button size="sm" variant="ghost" icon={<X className="size-3.5" />} onClick={() => onChange(null)}>{t("Entfernen")}</Button>}
        </div>
        <input
          ref={ref}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            if (f.size > 2 * 1024 * 1024) return void toast.error(t("Bild größer als 2 MB – bitte verkleinern."));
            setBusy(true);
            try {
              const { id } = await api.trackingPage.uploadAsset(tenantId, await readFile(f));
              onChange(id);
            } catch (err) {
              toast.error(errMsg(err));
            } finally {
              setBusy(false);
            }
          }}
        />
      </div>
    </Field>
  );
}

function BlockEditor({ b, lang, set, tenantId }: { b: TrackingBlock; lang: TrackingPageLang; set: (fn: (b: TrackingBlock) => TrackingBlock) => void; tenantId: string }) {
  const patch = <T extends TrackingBlock>(p: Partial<T>) => set((x) => ({ ...x, ...p }) as TrackingBlock);
  switch (b.type) {
    case "hero":
      return (
        <>
          <ImagePicker label={msg("Hintergrundbild")} value={b.image} onChange={(image) => patch({ image })} tenantId={tenantId} hint={msg("Querformat, mind. 1600 px breit. Ohne Bild erscheint nur der Text.")} />
          <LInput label={msg("Überschrift")} value={b.title} lang={lang} onChange={(title) => patch({ title })} max={120} />
          <LInput label={msg("Text")} value={b.text} lang={lang} onChange={(text) => patch({ text })} area max={400} />
          <div className="grid grid-cols-2 gap-2.5">
            <LInput label={msg("Button-Text")} value={b.buttonLabel} lang={lang} onChange={(buttonLabel) => patch({ buttonLabel })} max={40} />
            <UrlInput label={msg("Button-Link")} value={b.buttonUrl} onChange={(buttonUrl) => patch({ buttonUrl })} />
          </div>
          <Segmented label={msg("Höhe")} value={b.height} options={[["compact", msg("Kompakt")], ["tall", msg("Groß")]]} onChange={(height) => patch({ height })} />
        </>
      );
    case "status":
      return (
        <>
          <LInput label={msg("Überschrift (optional)")} value={b.headline} lang={lang} onChange={(headline) => patch({ headline })} max={120} />
          <Segmented label={msg("Darstellung")} value={b.style} options={[["steps", msg("Mit Fortschrittsleiste")], ["minimal", msg("Schlicht")]]} onChange={(style) => patch({ style })} />
          <Toggle label={msg("Sendungsverlauf anzeigen")} checked={b.showEvents} onChange={(showEvents) => patch({ showEvents })} />
          <p className="text-[11.5px] text-fg-subtle">{t("Status und Verlauf kommen automatisch vom Versanddienstleister. Name und Straße des Empfängers werden nie angezeigt – nur PLZ und Ort.")}</p>
        </>
      );
    case "eta":
      return (
        <>
          <LInput label={msg("Überschrift")} value={b.headline} lang={lang} onChange={(headline) => patch({ headline })} max={80} placeholder={msg("Voraussichtliche Zustellung")} />
          <p className="text-[11.5px] text-fg-subtle">{t("Berechnet aus Versandtag und Zielland, „Heute“ sobald das Paket in Zustellung ist. Nach der Zustellung steht hier das Zustelldatum.")}</p>
        </>
      );
    case "items":
      return (
        <>
          <LInput label={msg("Überschrift")} value={b.headline} lang={lang} onChange={(headline) => patch({ headline })} max={80} placeholder={msg("Paketinhalt")} />
          <Toggle label={msg("Produktbilder anzeigen")} checked={b.showImages} onChange={(showImages) => patch({ showImages })} />
          <p className="text-[11.5px] text-fg-subtle">{t("Bilder kommen aus den Artikel-Stammdaten. Beilagen aus Auftragsregeln und Preise werden nicht gezeigt.")}</p>
        </>
      );
    case "promo":
      return (
        <>
          <LInput label={msg("Titel")} value={b.title} lang={lang} onChange={(title) => patch({ title })} max={120} />
          <LInput label={msg("Text")} value={b.text} lang={lang} onChange={(text) => patch({ text })} area max={300} />
          <Field label={msg("Rabattcode")}>
            <Input value={b.code} maxLength={40} onChange={(e) => patch({ code: e.target.value.toUpperCase() })} className="font-mono" />
          </Field>
          <div className="grid grid-cols-2 gap-2.5">
            <LInput label={msg("Button-Text")} value={b.buttonLabel} lang={lang} onChange={(buttonLabel) => patch({ buttonLabel })} max={40} />
            <UrlInput label={msg("Button-Link")} value={b.buttonUrl} onChange={(buttonUrl) => patch({ buttonUrl })} />
          </div>
          <ImagePicker label={msg("Bild (optional)")} value={b.image} onChange={(image) => patch({ image })} tenantId={tenantId} />
          <ColorField label={msg("Hintergrund")} value={b.background} onChange={(background) => patch({ background })} allowDefault={msg("Akzentfarbe")} />
        </>
      );
    case "products":
      return (
        <>
          <LInput label={msg("Überschrift")} value={b.headline} lang={lang} onChange={(headline) => patch({ headline })} max={80} />
          {b.items.map((it, i) => (
            <div key={it.id} className="rounded-lg border border-border p-2.5 flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-fg-muted">{t("Produkt {n}", { n: i + 1 })}</span>
                <button type="button" aria-label={t("Entfernen")} onClick={() => patch({ items: b.items.filter((x) => x.id !== it.id) })} className="p-1 rounded text-fg-subtle hover:text-danger"><Trash2 className="size-3.5" /></button>
              </div>
              <ImagePicker label={msg("Bild")} value={it.image} onChange={(image) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, image } : x)) })} tenantId={tenantId} />
              <LInput label={msg("Name")} value={it.title} lang={lang} onChange={(title) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, title } : x)) })} max={80} />
              <div className="grid grid-cols-[110px_1fr] gap-2.5">
                <Field label={msg("Preis")}><Input value={it.price} maxLength={30} placeholder="19,90 €" onChange={(e) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, price: e.target.value } : x)) })} /></Field>
                <UrlInput label={msg("Link")} value={it.url} onChange={(url) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, url } : x)) })} />
              </div>
            </div>
          ))}
          {b.items.length < 8 && (
            <Button size="sm" variant="secondary" icon={<Plus className="size-3.5" />} onClick={() => patch({ items: [...b.items, { id: `p${Date.now().toString(36)}`, image: null, title: { de: "", en: "" }, price: "", url: "" }] })}>{t("Produkt hinzufügen")}</Button>
          )}
        </>
      );
    case "text":
      return (
        <>
          <LInput label={msg("Überschrift")} value={b.headline} lang={lang} onChange={(headline) => patch({ headline })} max={120} />
          <LInput label={msg("Text")} value={b.body} lang={lang} onChange={(body) => patch({ body })} area max={2000} />
          <Segmented label={msg("Ausrichtung")} value={b.align} options={[["left", msg("Links")], ["center", msg("Zentriert")]]} onChange={(align) => patch({ align })} />
        </>
      );
    case "faq":
      return (
        <>
          <LInput label={msg("Überschrift")} value={b.headline} lang={lang} onChange={(headline) => patch({ headline })} max={80} />
          {b.items.map((it, i) => (
            <div key={it.id} className="rounded-lg border border-border p-2.5 flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-fg-muted">{t("Frage {n}", { n: i + 1 })}</span>
                <button type="button" aria-label={t("Entfernen")} onClick={() => patch({ items: b.items.filter((x) => x.id !== it.id) })} className="p-1 rounded text-fg-subtle hover:text-danger"><Trash2 className="size-3.5" /></button>
              </div>
              <LInput label={msg("Frage")} value={it.q} lang={lang} onChange={(q) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, q } : x)) })} max={200} />
              <LInput label={msg("Antwort")} value={it.a} lang={lang} onChange={(a) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, a } : x)) })} area max={1500} />
            </div>
          ))}
          {b.items.length < 15 && (
            <Button size="sm" variant="secondary" icon={<Plus className="size-3.5" />} onClick={() => patch({ items: [...b.items, { id: `q${Date.now().toString(36)}`, q: { de: "", en: "" }, a: { de: "", en: "" } }] })}>{t("Frage hinzufügen")}</Button>
          )}
        </>
      );
    case "buttons":
      return (
        <>
          {b.items.map((it, i) => (
            <div key={it.id} className="rounded-lg border border-border p-2.5 flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-fg-muted">{t("Button {n}", { n: i + 1 })}</span>
                <button type="button" aria-label={t("Entfernen")} onClick={() => patch({ items: b.items.filter((x) => x.id !== it.id) })} className="p-1 rounded text-fg-subtle hover:text-danger"><Trash2 className="size-3.5" /></button>
              </div>
              <LInput label={msg("Text")} value={it.label} lang={lang} onChange={(label) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, label } : x)) })} max={40} />
              <UrlInput label={msg("Link")} value={it.url} onChange={(url) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, url } : x)) })} hint={msg("z. B. euer Retourenportal, mailto:service@… oder die Kontaktseite – ohne Link wird der Button nicht angezeigt")} />
              <Segmented label={msg("Stil")} value={it.style} options={[["primary", msg("Hervorgehoben")], ["secondary", msg("Dezent")]]} onChange={(style) => patch({ items: b.items.map((x) => (x.id === it.id ? { ...x, style } : x)) })} />
            </div>
          ))}
          {b.items.length < 4 && (
            <Button size="sm" variant="secondary" icon={<Plus className="size-3.5" />} onClick={() => patch({ items: [...b.items, { id: `a${Date.now().toString(36)}`, label: { de: "", en: "" }, url: "", style: "secondary" }] })}>{t("Button hinzufügen")}</Button>
          )}
        </>
      );
    case "social":
      return (
        <>
          <LInput label={msg("Überschrift")} value={b.headline} lang={lang} onChange={(headline) => patch({ headline })} max={80} placeholder={msg("Folge uns")} />
          {(["instagram", "tiktok", "facebook", "youtube", "pinterest", "x", "linkedin"] as const).map((k) => (
            <UrlInput key={k} label={k === "x" ? "X" : k === "tiktok" ? t("TikTok") : k === "youtube" ? t("YouTube") : k === "linkedin" ? t("LinkedIn") : k[0]!.toUpperCase() + k.slice(1)} value={b.links[k] ?? ""} onChange={(v) => patch({ links: { ...b.links, [k]: v } })} />
          ))}
        </>
      );
    case "newsletter":
      return (
        <>
          <LInput label={msg("Titel")} value={b.title} lang={lang} onChange={(title) => patch({ title })} max={120} />
          <LInput label={msg("Text")} value={b.text} lang={lang} onChange={(text) => patch({ text })} area max={300} />
          <div className="grid grid-cols-2 gap-2.5">
            <LInput label={msg("Button-Text")} value={b.buttonLabel} lang={lang} onChange={(buttonLabel) => patch({ buttonLabel })} max={40} />
            <UrlInput label={msg("Anmelde-Link")} value={b.buttonUrl} onChange={(buttonUrl) => patch({ buttonUrl })} />
          </div>
        </>
      );
    case "image":
      return (
        <>
          <ImagePicker label={msg("Bild")} value={b.image} onChange={(image) => patch({ image })} tenantId={tenantId} />
          <UrlInput label={msg("Link (optional)")} value={b.url} onChange={(url) => patch({ url })} />
          <LInput label={msg("Bildbeschreibung (Barrierefreiheit)")} value={b.alt} lang={lang} onChange={(alt) => patch({ alt })} max={120} />
        </>
      );
  }
}

function Segmented<V extends string>({ label, value, options, onChange }: { label: string; value: V; options: readonly (readonly [V, string])[]; onChange: (v: V) => void }) {
  return (
    <div>
      <p className="text-[12px] font-medium text-fg-muted mb-1">{t(label)}</p>
      <div className="flex rounded-lg bg-surface-2 p-0.5 text-[12.5px]">
        {options.map(([k, l]) => (
          <button key={k} type="button" onClick={() => onChange(k)} className={cx("flex-1 px-2 py-1.5 rounded-md font-medium", value === k ? "bg-surface shadow-sm text-fg" : "text-fg-muted hover:text-fg")}>{t(l)}</button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className="flex items-start gap-2.5 text-left">
      <span className={cx("mt-0.5 h-[18px] w-8 shrink-0 rounded-full p-0.5 transition-colors", checked ? "bg-ink" : "bg-border-strong")}>
        <span className={cx("block size-3.5 rounded-full bg-surface transition-transform", checked && "translate-x-3.5")} />
      </span>
      <span className="text-[13px]">
        <span className="font-medium">{t(label)}</span>
        {hint && <span className="block text-[12px] text-fg-subtle">{t(hint)}</span>}
      </span>
    </button>
  );
}

function ColorField({ label, value, onChange, allowDefault }: { label: string; value: string | null; onChange: (v: string | null) => void; allowDefault?: string }) {
  const [text, setText] = useState(value ?? "");
  useEffect(() => setText(value ?? ""), [value]);
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input type="color" value={value ?? "#f97361"} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 cursor-pointer rounded-lg border border-border bg-surface p-1" aria-label={t(label)} />
        <Input
          value={text}
          placeholder={allowDefault ? t(allowDefault) : "#111827"}
          className="font-mono"
          onChange={(e) => {
            setText(e.target.value);
            if (/^#[0-9a-f]{6}$/i.test(e.target.value)) onChange(e.target.value.toLowerCase());
            else if (!e.target.value && allowDefault) onChange(null);
          }}
        />
      </div>
    </Field>
  );
}

// ── Design ──

function DesignPanel({ cfg, lang, update, tenantId }: { cfg: TrackingPageConfig; lang: TrackingPageLang; update: (fn: (c: TrackingPageConfig) => TrackingPageConfig) => void; tenantId: string }) {
  const th = cfg.theme;
  const setTheme = (p: Partial<TrackingPageConfig["theme"]>) => update((c) => ({ ...c, theme: { ...c.theme, ...p } }));
  return (
    <div className="p-4 flex flex-col gap-5">
      <ImagePicker label={msg("Logo")} value={th.logo} onChange={(logo) => setTheme({ logo })} tenantId={tenantId} hint={msg("PNG mit transparentem Hintergrund, etwa 400 × 120 px. Ohne Logo steht der Shopname.")} />
      <UrlInput label={msg("Link hinter dem Logo (euer Shop)")} value={th.shopUrl} onChange={(shopUrl) => setTheme({ shopUrl })} />
      <div className="grid grid-cols-2 gap-3">
        <ColorField label={msg("Markenfarbe")} value={th.brand} onChange={(v) => v && setTheme({ brand: v })} />
        <ColorField label={msg("Akzentfarbe")} value={th.accent} onChange={(v) => v && setTheme({ accent: v })} />
      </div>
      <div>
        <p className="text-[12px] font-medium text-fg-muted mb-1.5">{t("Hintergrund")}</p>
        <div className="grid grid-cols-4 gap-2">
          {BACKGROUNDS.map((bg) => (
            <button key={bg.id} type="button" onClick={() => setTheme({ background: bg.id })} className={cx("rounded-xl border p-1.5 text-[11.5px] font-medium transition-colors", th.background === bg.id ? "border-ink ring-2 ring-ink/10" : "border-border hover:border-border-strong")}>
              <span
                className="block h-12 rounded-lg border border-black/5"
                style={{
                  background: bg.id === "dark" ? "#0b0b0e" : bg.id === "light" ? "#ffffff" : bg.id === "brand" ? `linear-gradient(180deg, ${th.brand} 0 45%, #f6f5f3 45%)` : `radial-gradient(120px 50px at 50% 0, ${th.brand}33, transparent), #f7f6f4`,
                }}
              />
              <span className="mt-1 block">{t(bg.label)}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[12px] font-medium text-fg-muted mb-1.5">{t("Schrift")}</p>
        <div className="grid grid-cols-4 gap-2">
          {FONTS.map((f) => (
            <button key={f.id} type="button" onClick={() => setTheme({ font: f.id })} className={cx("rounded-xl border px-2 py-2.5 transition-colors", th.font === f.id ? "border-ink ring-2 ring-ink/10" : "border-border hover:border-border-strong")}>
              <span className="block text-[22px] leading-none" style={{ fontFamily: f.family, fontWeight: 700 }}>{t("Aa")}</span>
              <span className="mt-1 block text-[11.5px] font-medium text-fg-muted">{t(f.label)}</span>
            </button>
          ))}
        </div>
      </div>
      <Segmented label={msg("Ecken")} value={th.radius} options={RADII.map((r) => [r.id, r.label] as const)} onChange={(radius) => setTheme({ radius })} />
      <LInput label={msg("Seitentitel (Browser-Tab)")} value={cfg.title} lang={lang} onChange={(title) => update((c) => ({ ...c, title }))} max={80} />
      <LInput label={msg("Fußzeile")} value={th.footer} lang={lang} onChange={(footer) => setTheme({ footer })} area max={300} />
      <div className="grid grid-cols-2 gap-3">
        <UrlInput label={msg("Impressum")} value={th.imprintUrl} onChange={(imprintUrl) => setTheme({ imprintUrl })} />
        <UrlInput label={msg("Datenschutz")} value={th.privacyUrl} onChange={(privacyUrl) => setTheme({ privacyUrl })} />
      </div>
      <p className="text-[11.5px] text-fg-subtle">{t("Impressum und Datenschutzerklärung sind für eine Seite unter eurem Namen Pflicht – bitte verlinken.")}</p>
    </div>
  );
}

// ── Einstellungen ──

function SettingsPanel(p: {
  d: TrackingPageAdmin; editable: boolean; linkInShop: boolean; searchEnabled: boolean; slug: string;
  onLinkInShop: (v: boolean) => void; onSearch: (v: boolean) => void; onSlug: (v: string) => void; onUnpublish: () => void; tenantId: string;
}) {
  const { d } = p;
  const qc = useQueryClient();
  const [domain, setDomain] = useState(d.domain.name ?? "");
  useEffect(() => setDomain(d.domain.name ?? ""), [d.domain.name]);
  const setDom = useMutation({
    mutationFn: (v: string | null) => api.trackingPage.setDomain(p.tenantId, v),
    onSuccess: (x) => qc.setQueryData(["tracking-page", p.tenantId], x),
    onError: (e) => toast.error(errMsg(e)),
  });
  const check = useMutation({
    mutationFn: () => api.trackingPage.checkDomain(p.tenantId),
    onSuccess: (x) => qc.setQueryData(["tracking-page", p.tenantId], x),
    onError: (e) => toast.error(errMsg(e)),
  });
  const host = d.domain.name?.split(".")[0] ?? "track";
  const st = DOMAIN_STATUS[d.domain.status]!;
  const copy = (v: string) => void navigator.clipboard?.writeText(v).then(() => toast.success(t("Kopiert")));

  return (
    <div className="p-4 flex flex-col gap-6">
      <section>
        <h3 className="text-[13.5px] font-semibold mb-2.5">{t("So kommen Endkunden auf die Seite")}</h3>
        <div className="flex flex-col gap-3">
          <Toggle label={msg("Link in der Shopify-Versandmail")} hint={msg("Beim Versand meldet Logiful statt des DHL-Links den persönlichen Link zu eurer Seite an Shopify – die Versandbestätigung eures Shops führt dann direkt hierher.")} checked={p.linkInShop} onChange={p.onLinkInShop} />
          <Toggle label={msg("Suche nach Bestellnummer")} hint={msg("Auf der Startseite der Tracking-Seite: Bestellnummer + PLZ oder E-Mail. Ideal zum Verlinken im Shop-Menü oder Footer.")} checked={p.searchEnabled} onChange={p.onSearch} />
        </div>
      </section>

      <section>
        <h3 className="text-[13.5px] font-semibold mb-2.5">{t("Adresse")}</h3>
        <Field label={msg("Kurzname")} hint={msg("Kann nicht mehr geändert werden, sobald Links an Endkunden verschickt wurden.")}>
          <Input value={p.slug} onChange={(e) => p.onSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} leading={<span className="text-fg-subtle text-[12px] whitespace-nowrap">{d.defaultUrl.replace(/^https:\/\//, "").replace(/[^/]+$/, "")}</span>} maxLength={40} />
        </Field>
        <div className="mt-2.5 flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2 text-[12.5px]">
          <Globe className="size-4 text-fg-subtle shrink-0" />
          <span className="font-mono truncate flex-1">{d.publicUrl}</span>
          <button type="button" aria-label={t("Kopieren")} onClick={() => copy(d.publicUrl)} className="text-fg-subtle hover:text-fg"><Copy className="size-3.5" /></button>
          {d.published && <a href={d.publicUrl} target="_blank" rel="noopener noreferrer" className="text-fg-subtle hover:text-fg" aria-label={t("Öffnen")}><Eye className="size-3.5" /></a>}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="text-[13.5px] font-semibold">{t("Eigene Domain")}</h3>
          {d.domain.status !== "none" && <Badge tone={st.tone}>{t(st.label)}</Badge>}
        </div>
        {!d.features.customDomain ? (
          <p className="text-[12.5px] text-fg-muted">{t("Mit dem Paket „Tracking-Seite mit eigener Domain“ läuft die Seite unter eurer Adresse, z. B. track.dein-shop.de. Logiful schaltet das für euch frei.")}</p>
        ) : (
          <>
            <div className="flex gap-2">
              <Input value={domain} placeholder={t("track.dein-shop.de")} onChange={(e) => setDomain(e.target.value)} className="font-mono" />
              <Button variant="secondary" disabled={!p.editable || setDom.isPending || !domain.trim() || domain.trim().toLowerCase() === d.domain.name} onClick={() => setDom.mutate(domain.trim())}>{t("Übernehmen")}</Button>
            </div>
            {d.domain.name && (
              <div className="mt-3 rounded-xl border border-border p-3">
                {d.domain.status !== "active" && (
                  <>
                    <p className="text-[12.5px] text-fg-muted mb-2">{t("Lege bei deinem Domain-Anbieter diesen DNS-Eintrag an:")}</p>
                    <div className="grid grid-cols-[auto_auto_1fr] gap-x-4 gap-y-1 text-[12.5px] font-mono rounded-lg bg-surface-2 px-3 py-2">
                      <span className="text-fg-subtle font-sans">{t("Typ")}</span>
                      <span className="text-fg-subtle font-sans">{t("Name")}</span>
                      <span className="text-fg-subtle font-sans">{t("Ziel")}</span>
                      <span>{t("CNAME")}</span>
                      <span>{host}</span>
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{d.domain.cnameTarget}</span>
                        <button type="button" aria-label={t("Kopieren")} onClick={() => copy(d.domain.cnameTarget)} className="text-fg-subtle hover:text-fg"><Copy className="size-3" /></button>
                      </span>
                    </div>
                  </>
                )}
                {d.domain.status === "active" ? (
                  <p className="flex items-center gap-1.5 text-[12.5px] text-success"><CheckCircle2 className="size-4" />{t("Aktiv mit SSL-Zertifikat – neue Versandmails verlinken {domain}.", { domain: d.domain.name })}</p>
                ) : (
                  d.domain.message && <p className="mt-2 text-[12px] text-fg-muted">{t(d.domain.message)}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {d.domain.status !== "active" && <Button size="sm" variant="secondary" icon={<RefreshCw className={cx("size-3.5", check.isPending && "animate-spin")} />} disabled={check.isPending} onClick={() => check.mutate()}>{t("Jetzt prüfen")}</Button>}
                  <Button size="sm" variant="ghost" disabled={!p.editable || setDom.isPending} onClick={() => { if (confirm(t("Eigene Domain entfernen? Bereits verschickte Links mit dieser Domain funktionieren dann nicht mehr."))) setDom.mutate(null); }}>{t("Domain entfernen")}</Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <section>
        <h3 className="text-[13.5px] font-semibold mb-1.5">{t("Was Endkunden sehen")}</h3>
        <p className="text-[12.5px] text-fg-muted">{t("Status, Sendungsverlauf, Paketinhalt und den Zielort (PLZ und Ort). Name, Straße, E-Mail und Preise erscheinen nie – auch nicht, wenn der Link weitergeleitet wird.")}</p>
      </section>

      {d.published && p.editable && (
        <section className="border-t border-border pt-4">
          <Button variant="ghost" className="text-danger" onClick={() => { if (confirm(t("Seite offline nehmen? Neue Versandmails verlinken dann wieder den Versanddienstleister, bestehende Links zeigen eine Hinweisseite."))) p.onUnpublish(); }}>
            {t("Seite offline nehmen")}
          </Button>
        </section>
      )}
      {isDemo && <p className="text-[11.5px] text-fg-subtle">{t("Demo: Domainprüfung wird simuliert.")}</p>}
    </div>
  );
}
